import { AgGridReact, AgGridReactProps } from "ag-grid-react";
import {
  ColDef,
  ColGroupDef,
  ColumnVisibleEvent,
  GridReadyEvent,
  StateUpdatedEvent
} from "ag-grid-community";
import cns from "classnames";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from "react";
import locale from "@bonliva-crm/locale";
import { Typography, makeStyles, Paper, IconButton, Tooltip } from "@material-ui/core";
import MultiSelect from "../select/multi-select";
import { Loader } from "../../../utils";
import { sortSelectedFirst } from "../select/utils/sort-selected-first";
import { ColumnNameSelector } from "@consultants/consultants-search-new/components/consultant-table/use-column-name-selector";
import { Refresh } from "@material-ui/icons";

type Props = {
  loading?: boolean;
  topBar?: React.ReactNode;
  columnNameSelector?: ColumnNameSelector;
  sortedColumn?: string;
  setDefaultSort?: () => void;
} & Omit<AgGridReactProps, "ref">;

export const DataGrid = forwardRef<AgGridReact, Props>((props, forwardedRef) => {
  const gridRef = useRef<AgGridReact>(null);
  useImperativeHandle(forwardedRef, () => gridRef.current as AgGridReact);
  const [showGrid, setShowGrid] = useState(true);
  const reloadGrid = useCallback(() => {
    setShowGrid(false);
    setTimeout(() => {
      setShowGrid(true);
    });
  }, []);

  const { loading, ...gridProps } = props;

  useEffect(() => {
    if (gridRef.current?.api) {
      if (loading) {
        setTimeout(() => {
          if (gridRef.current?.api && loading) {
            gridRef.current.api.showLoadingOverlay();
          }
        }, 10);
      } else if (!gridRef.current?.api.paginationGetTotalPages()) {
        gridRef.current.api.showNoRowsOverlay();
      } else {
        gridRef.current.api.hideOverlay();
      }
    }
  }, [loading, gridRef.current?.api]);

  const loadingOverlayComponent = useMemo(() => {
    return Loader;
  }, []);

  const noRowsOverlayComponent = useMemo(() => {
    return () => (
      <div>
        <Typography variant="h6">{locale.NoSearchResults}</Typography>
      </div>
    );
  }, []);

  const localStorageColumns = props.columnNameSelector?.localStorageName
    ? localStorage.getItem(props.columnNameSelector?.localStorageName)
    : undefined;

  const [visibleColumns, _setVisibleColumns] = useState<string[]>(
    localStorageColumns
      ? JSON.parse(localStorageColumns)
      : props.columnNameSelector?.defaultColumns.map((c) => c.value) ?? []
  );

  const setVisibleColumns = useCallback(
    (columns: string[]) => {
      if (props.columnNameSelector?.localStorageName) {
        localStorage.setItem(
          props.columnNameSelector.localStorageName,
          JSON.stringify(columns)
        );
      }

      _setVisibleColumns(columns);
    },
    [props.columnNameSelector?.defaultColumns, props.columnNameSelector?.localStorageName]
  );

  useEffect(() => {
    if (props.columnNameSelector?.localStorageName) {
      const localStorageColumns = localStorage.getItem(
        props.columnNameSelector?.localStorageName
      );
      reloadGrid();

      if (localStorageColumns) {
        _setVisibleColumns(JSON.parse(localStorageColumns));
      } else {
        _setVisibleColumns(
          props.columnNameSelector?.defaultColumns.map((c) => c.value as string) ?? []
        );
      }
    }
  }, [props.columnNameSelector?.localStorageName]);

  const onStateUpdated = useCallback(
    (event: StateUpdatedEvent<any, any>) => {
      gridProps.onStateUpdated && gridProps.onStateUpdated(event);
      if (event.sources.includes("gridInitializing")) return;
      props.columnNameSelector?.localStorageName &&
        localStorage.setItem(
          `${props.columnNameSelector.localStorageName}_gridState`,
          JSON.stringify(event.state)
        );
    },
    [props.columnNameSelector?.localStorageName]
  );

  const initialGridState = useMemo(() => {
    const gridState = props.columnNameSelector?.localStorageName
      ? localStorage.getItem(`${props.columnNameSelector.localStorageName}_gridState`)
      : undefined;
    return gridState ? JSON.parse(gridState) : undefined;
  }, [props.columnNameSelector?.localStorageName]);

  const onGridReady = useCallback(
    (event: GridReadyEvent) => {
      gridProps.onGridReady && gridProps.onGridReady(event);
      if (!props.columnNameSelector) return;

      const allColumns = event.api.getColumns();
      if (!allColumns) return;

      const columnsToShow: string[] = [];
      const columnsToHide: string[] = [];
      allColumns?.forEach((column) => {
        if (
          column.getColDef().lockVisible ||
          visibleColumns.includes(column.getColId())
        ) {
          !column.isVisible() && columnsToShow.push(column.getColId());
        } else {
          column.isVisible() && columnsToHide.push(column.getColId());
        }
      });
      columnsToShow.length > 0 && event.api.setColumnsVisible(columnsToShow, true);
      columnsToHide.length > 0 && event.api.setColumnsVisible(columnsToHide, false);
    },
    [props.columnNameSelector, visibleColumns, gridProps]
  );

  const onColumnVisible = useCallback(
    (event: ColumnVisibleEvent) => {
      if (!props.columnNameSelector) return;
      if (!event.columns) return;

      if (event.visible) {
        const columnsToShow = event.columns.filter(
          (col) => !visibleColumns.includes(col.getColId())
        );

        columnsToShow.length &&
          setVisibleColumns([
            ...visibleColumns,
            ...columnsToShow.map((col) => col.getColId())
          ]);
      } else {
        const columnsToHide = event.columns.filter((col) =>
          visibleColumns.includes(col.getColId())
        );

        columnsToHide.length &&
          setVisibleColumns(
            visibleColumns.filter(
              (columnId) => !columnsToHide.some((c) => c.getColId() === columnId)
            )
          );

        // if sortColumn is hidden, reset sortColumn
        if (props.sortedColumn && !visibleColumns.includes(props.sortedColumn)) {
          props.setDefaultSort && props.setDefaultSort();
        }
      }
    },
    [props.columnNameSelector, setVisibleColumns, visibleColumns]
  );

  const onFirstDataRendered = useCallback(() => {
    if (gridRef.current?.api) {
      gridRef.current.api.hideOverlay();
    }
  }, []);

  useEffect(() => {
    if (gridRef.current?.api) {
      const allColumns = gridRef.current.api.getColumns();
      if (!allColumns) return;

      const columnsToShow: string[] = [];
      const columnsToHide: string[] = [];
      allColumns?.forEach((column) => {
        if (
          column.getColDef().lockVisible ||
          visibleColumns.includes(column.getColId())
        ) {
          !column.isVisible() && columnsToShow.push(column.getColId());
        } else {
          column.isVisible() && columnsToHide.push(column.getColId());
        }
      });

      columnsToShow.length > 0 &&
        gridRef.current.api.setColumnsVisible(columnsToShow, true);
      columnsToHide.length > 0 &&
        gridRef.current.api.setColumnsVisible(columnsToHide, false);
    }
  }, [visibleColumns]);

  const columnHeaderOptions = useMemo(() => {
    return (
      props.columnDefs?.map((col) => ({
        label: col.headerName || "",
        value: (col as ColDef).field
          ? (col as ColDef).field
          : (col as ColGroupDef).groupId
      })) || []
    );
  }, [props.columnNameSelector?.defaultColumns, props.columnDefs]);

  const classes = useStyles();

  const forceRefresh = () => {
    if (gridRef.current?.api) {
      gridRef.current.api.refreshInfiniteCache();
    }
  };

  return (
    showGrid && (
      <div className={classes.root}>
        <Paper className={classes.topBar} square elevation={0}>
          {props.columnNameSelector && (
            <div className={classes.columnSelector}>
              <MultiSelect<{ label: string; value: any }>
                options={columnHeaderOptions}
                value={visibleColumns.map((value) => ({
                  label: columnHeaderOptions.find((c) => c.value === value)?.label ?? "",
                  value
                }))}
                onChange={(selected) => {
                  setVisibleColumns(selected.map((value) => value.value));
                }}
                hideSelectedOptions={false}
                closeMenuOnSelect={false}
                optionType="checkbox"
                styleProps={{
                  control: { showBorder: true },
                  menu: { showBorder: true }
                }}
                controlShouldRenderValue={false}
                placeholder={locale.columns}
                isSearchable={true}
                showOptionDivider={false}
                hidePlaceholderOnFocus={false}
                isClearable={true}
                backspaceRemovesValue={false}
                onClear={() => setVisibleColumns([])}
                sortOptionsOnMenuOpenFn={(options) =>
                  sortSelectedFirst({
                    options,
                    value: visibleColumns,
                    valueOptionEqualityFn: (value, option) => value === option.value
                  })
                }
              />
            </div>
          )}
          {props.topBar && props.topBar}
          <Tooltip title={locale.forceRefresh}>
            <IconButton onClick={forceRefresh}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Paper>

        <div className={cns("ag-theme-quartz", classes.gridContainer)}>
          <AgGridReact
            ref={gridRef}
            loadingOverlayComponent={loadingOverlayComponent}
            noRowsOverlayComponent={noRowsOverlayComponent}
            onColumnVisible={onColumnVisible}
            onFirstDataRendered={onFirstDataRendered}
            {...gridProps}
            onGridReady={onGridReady}
            onStateUpdated={onStateUpdated}
            initialState={initialGridState}
            localeText={locale.agGrid}
          />
        </div>
      </div>
    )
  );
});

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    height: "100%",
    position: "relative"
  },
  topBar: {
    width: "100%",
    display: "flex",
    alignItems: "start",
    gap: theme.spacing(1),
    marginBottom: "-0.3rem",
    padding: "0.3rem 0.3rem 0.6rem 0.3rem"
  },
  columnSelector: {
    width: "300px",
    backgroundColor: theme.palette.background.default
  },
  gridContainer: {
    height: "100%",
    width: "100%",

    "& .ag-pinned-left-header": {
      borderRight: "none"
    },
    "& .ag-row-hover": {
      cursor: "pointer"
    },
    "& .ag-cell-value": {
      lineHeight: theme.typography.body2.lineHeight,
      border: 0
    },
    "& .ag-cell": {
      paddingTop: "4px",
      paddingBottom: "4px",
      display: "flex",
      alignItems: "center"
    },
    "--ag-cell-horizontal-padding": "5px",
    "--ag-font-size": theme.typography.body2.fontSize,
    "--ag-font-family": theme.typography.body2.fontFamily,
    "--ag-font-style": theme.typography.body2.fontStyle,
    "--ag-line-height": theme.typography.body2.lineHeight,
    "& .ag-checkbox-input-wrapper.ag-disabled": {
      opacity: 0.2
    },
    "& .ag-checkbox-input-wrapper.ag-disabled:focus": {
      outline: "none",
      boxShadow: "none"
    },
    "& .ag-checkbox-input-wrapper.ag-disabled:active": {
      outline: "none",
      boxShadow: "none"
    }
  }
}));
